import React, { useState } from "react";
import { Download, RefreshCw } from "lucide-react";
import { downloadInvoice } from "../../services/billingService";

export default function LastInvoiceSummaryCard({ invoice }) {
  const [downloading, setDownloading] = useState(false);

  /* ------------------------------------------------------------------------ */
  /* Empty State                                                              */
  /* ------------------------------------------------------------------------ */

  if (!invoice) {
    return (
      <div className="rounded-2xl border bg-white p-6 text-sm text-gray-500">
        No invoices generated yet.
      </div>
    );
  }

  /* ------------------------------------------------------------------------ */
  /* Destructure                                                              */
  /* ------------------------------------------------------------------------ */

  const {
    _id,
    invoiceNumber,
    plan,
    baseAmount,
    gstRate,
    gstAmount,
    totalAmount,
    currency = "INR",
    status,
    invoiceDate,
    pdfPath,
    pdfUrl,
  } = invoice;

  /* ------------------------------------------------------------------------ */
  /* GST                                                                       */
  /* ------------------------------------------------------------------------ */

  const normalizedGstRate = Number(gstRate) || 0;

  const gstEnabled = normalizedGstRate > 0;

  /* ------------------------------------------------------------------------ */
  /* Status Mapping                                                           */
  /* ------------------------------------------------------------------------ */

  const statusMeta =
    {
      paid: {
        label: "PAID",
        className:
          "bg-emerald-100 text-emerald-700",
      },

      sent: {
        label: "AWAITING PAYMENT",
        className:
          "bg-amber-100 text-amber-700",
      },

      unpaid: {
        label: "AWAITING PAYMENT",
        className:
          "bg-amber-100 text-amber-700",
      },

      overdue: {
        label: "OVERDUE",
        className:
          "bg-rose-100 text-rose-700",
      },

      cancelled: {
        label: "CANCELLED",
        className:
          "bg-gray-100 text-gray-700",
      },
    }[status] || {
      label:
        status?.toUpperCase() ||
        "UNKNOWN",

      className:
        "bg-gray-100 text-gray-700",
    };

  const canDownload =
    Boolean(pdfPath || pdfUrl);

  /* ------------------------------------------------------------------------ */
  /* Handlers                                                                 */
  /* ------------------------------------------------------------------------ */

  const handleDownload = async () => {
    if (downloading || !_id) {
      return;
    }

    try {
      setDownloading(true);

      await downloadInvoice(_id);

    } catch (err) {
      console.error(
        "[LastInvoiceSummaryCard] Invoice download failed:",
        err
      );

      alert(
        "Failed to download invoice"
      );

    } finally {
      setDownloading(false);
    }
  };

  /* ------------------------------------------------------------------------ */
  /* Render                                                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <div className="space-y-4 rounded-2xl border bg-white p-6">

      {/* ================= Header ================= */}

      <div className="flex items-center justify-between">

        <h2 className="text-lg font-semibold text-gray-900">
          Last Invoice
        </h2>

        <span
          className={`rounded px-2 py-1 text-xs ${statusMeta.className}`}
        >
          {statusMeta.label}
        </span>

      </div>

      {/* ================= Meta Info ================= */}

      <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-3">

        <Info
          label="Invoice Number"
          value={invoiceNumber}
        />

        <Info
          label="Invoice Date"
          value={
            invoiceDate
              ? new Date(
                  invoiceDate
                ).toLocaleDateString()
              : "—"
          }
        />

        <Info
          label="Plan"
          value={
            plan?.toUpperCase()
          }
        />

      </div>

      {/* ================= Amounts ================= */}

      <div
        className={`grid grid-cols-1 gap-4 text-sm ${
          gstEnabled
            ? "sm:grid-cols-3"
            : "sm:grid-cols-2"
        }`}
      >

        <Info
          label="Base Amount"
          value={`${currency} ${baseAmount}`}
        />

        {gstEnabled && (
          <Info
            label={`GST (${normalizedGstRate}%)`}
            value={`${currency} ${gstAmount}`}
          />
        )}

        <Info
          label="Total Amount"
          value={`${currency} ${totalAmount}`}
        />

      </div>

      {/* ================= Actions ================= */}

      <div className="pt-2">

        {canDownload ? (

          <button
            type="button"
            onClick={handleDownload}
            disabled={
              downloading
            }
            className="inline-flex items-center gap-2 rounded border px-3 py-2 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >

            <Download
              size={16}
            />

            {downloading
              ? "Downloading..."
              : "Download Invoice"}

          </button>

        ) : (

          <div className="flex items-center gap-2 text-xs text-gray-500">

            <RefreshCw
              size={14}
              className="animate-spin"
            />

            Generating invoice PDF...

          </div>

        )}

      </div>

    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* UI Helper                                                                  */
/* -------------------------------------------------------------------------- */

const Info = ({
  label,
  value,
}) => (
  <div>

    <div className="text-xs text-gray-500">
      {label}
    </div>

    <div className="font-medium text-gray-900">
      {value ?? "—"}
    </div>

  </div>
);